import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Wallet, PiggyBank, DollarSign } from 'lucide-react';
import { Card } from '../ui/card';
import { OdometerValue } from './OdometerValue';

const iconMap = {
    total: Wallet,
    available: DollarSign,
    invested: PiggyBank,
};

export const BalanceCard = ({ title, amount, currency = 'USD', type = 'total', trend = null, delay = 0 }) => {
    const Icon = iconMap[type] || Wallet;

    const formatAmount = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
        }).format(value);
    };

    const accentByType = {
        total:     { iconBg: 'rgba(212, 175, 55, 0.14)',  iconColor: '#e8d48b', bar: 'linear-gradient(90deg, #8a6a1a, #f9e27a, #d4af37)' },
        available: { iconBg: 'rgba(201, 201, 207, 0.12)', iconColor: '#e0e0e5', bar: 'linear-gradient(90deg, #8a8a94, #ffffff, #a8a8b0)' },
        invested:  { iconBg: 'rgba(16, 185, 129, 0.12)', iconColor: '#34d399', bar: 'linear-gradient(90deg, #059669, #6ee7b7, #10b981)' },
    };
    const accent = accentByType[type] || accentByType.total;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay }}
        >
            <Card
                className="relative overflow-hidden p-0 border-0 transition-shadow duration-300 hover:shadow-[0_8px_32px_rgba(212,175,55,0.18)]"
                style={{
                    background: 'linear-gradient(180deg, #141417 0%, #0c0c0e 100%)',
                    borderRadius: '14px',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(201,201,207,0.14), 0 14px 40px rgba(0,0,0,0.45)',
                }}
                data-testid={`balance-card-${type}`}
            >
                {/* Top accent bar */}
                <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: accent.bar }} />

                <div className="p-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                            <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ background: accent.iconBg }}
                            >
                                <Icon className="w-5 h-5" style={{ color: accent.iconColor }} />
                            </div>
                            <span
                                className="tracking-[0.06em] uppercase"
                                style={{ fontSize: '11px', fontWeight: 600, color: '#a3a3ab' }}
                            >
                                {title}
                            </span>
                        </div>
                        {trend !== null && (
                            <div
                                className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md`}
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    background: trend >= 0 ? 'rgba(16, 185, 129, 0.10)' : 'rgba(239, 68, 68, 0.10)',
                                    color: trend >= 0 ? '#6ee7b7' : '#fca5a5',
                                }}
                            >
                                {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {Math.abs(trend)}%
                            </div>
                        )}
                    </div>

                    <div
                        className="font-numbers"
                        data-testid={`balance-${type}-amount`}
                        style={{
                            fontSize: '34px',
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '-0.025em',
                            lineHeight: 1.1,
                            color: '#f3f3f5',
                        }}
                    >
                        <OdometerValue
                            value={formatAmount(amount)}
                            staggerMs={50}
                            duration={1.4}
                        />
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'rgba(201,201,207,0.12)' }}>
                        <p className="text-[11px] tracking-[0.06em]" style={{ color: '#8a8a94', fontWeight: 500 }}>
                            {currency === 'USD' ? 'US Dollar' : 'Euro'}
                        </p>
                        <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: '#c9c9cf', fontWeight: 600 }}>
                            {currency}
                        </span>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};
